import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { API, xhrJson, authHeader } from '@/lib/api';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/button';
import { Loader2, Settings, Save, Check, AlertCircle, Lock, Link as LinkIcon, RefreshCw, Copy } from 'lucide-react';

export default function ManagerSettings() {
  const { session } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hours, setHours] = useState('24');
  const [creditPerVideo, setCreditPerVideo] = useState('1');
  const [notes, setNotes] = useState('');
  const [updatedAt, setUpdatedAt] = useState(null);
  const [feedback, setFeedback] = useState(null); // {type:'ok'|'err', msg}

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!session?.access_token) { setLoading(false); return; }
      const { ok, data } = await xhrJson('GET', `${API}/api/admin/settings`, null, authHeader(session));
      if (cancelled) return;
      if (ok && data?.settings) {
        const s = data.settings;
        setHours(String(s.delivery_deadline_default_hours ?? 24));
        setCreditPerVideo(String(s.credit_per_video_default ?? 1));
        setNotes(s.notes || '');
        setUpdatedAt(s.updated_at || null);
      } else {
        setFeedback({ type: 'err', msg: data?.detail || "Couldn't load settings." });
      }
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [session?.access_token]);

  const handleSave = async () => {
    setSaving(true);
    setFeedback(null);
    const payload = {
      delivery_deadline_default_hours: parseInt(hours, 10),
      credit_per_video_default: parseInt(creditPerVideo, 10),
      notes: notes || null,
    };
    if (!Number.isFinite(payload.delivery_deadline_default_hours) ||
        payload.delivery_deadline_default_hours < 1 ||
        payload.delivery_deadline_default_hours > 720) {
      setFeedback({ type: 'err', msg: 'SLA must be between 1 and 720 hours.' });
      setSaving(false);
      return;
    }
    if (!Number.isFinite(payload.credit_per_video_default) ||
        payload.credit_per_video_default < 1 ||
        payload.credit_per_video_default > 100) {
      setFeedback({ type: 'err', msg: 'Credits per video must be between 1 and 100.' });
      setSaving(false);
      return;
    }
    const { ok, data } = await xhrJson('PATCH', `${API}/api/admin/settings`, payload, authHeader(session));
    if (ok) {
      setFeedback({ type: 'ok', msg: 'Settings saved.' });
      setUpdatedAt(data?.settings?.updated_at || new Date().toISOString());
    } else {
      setFeedback({ type: 'err', msg: data?.detail || "Couldn't save." });
    }
    setSaving(false);
  };

  return (
    <div className="flex flex-col h-full" data-testid="manager-settings">
      <div className="px-4 md:px-6 py-4 border-b border-gray-200/60 flex items-center gap-2">
        <Settings className="w-4 h-4 text-gray-400" />
        <h1 className="text-base md:text-lg font-medium text-gray-900 font-['Outfit']">General settings</h1>
      </div>

      <ScrollArea className="flex-1">
        <div className="p-4 md:p-6 max-w-2xl space-y-5">
          {loading ? (
            <div className="flex items-center justify-center py-16 text-gray-300">
              <Loader2 className="w-5 h-5 animate-spin" />
            </div>
          ) : (
            <>
              <div className="border border-gray-200/60 rounded-2xl p-5 bg-white/40 backdrop-blur-sm space-y-4">
                <div>
                  <label className="text-[11px] uppercase tracking-[0.15em] text-gray-400 block mb-1.5">
                    Universal delivery time (SLA)
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      data-testid="settings-sla-hours"
                      type="number"
                      min={1}
                      max={720}
                      value={hours}
                      onChange={(e) => setHours(e.target.value)}
                      className="w-28 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-900 outline-none focus:border-gray-300 transition-colors tabular-nums"
                    />
                    <span className="text-sm text-gray-500">hours</span>
                  </div>
                  <p className="text-[11px] text-gray-400 mt-1.5 leading-relaxed">
                    Default deadline when starting production. Automatically applied to each new project.
                  </p>
                </div>

                <div className="border-t border-gray-100 pt-4">
                  <label className="text-[11px] uppercase tracking-[0.15em] text-gray-400 block mb-1.5">
                    Credits per video
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      data-testid="settings-credit-per-video"
                      type="number"
                      min={1}
                      max={100}
                      value={creditPerVideo}
                      onChange={(e) => setCreditPerVideo(e.target.value)}
                      className="w-28 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-900 outline-none focus:border-gray-300 transition-colors tabular-nums"
                    />
                    <span className="text-sm text-gray-500">credits</span>
                  </div>
                  <p className="text-[11px] text-gray-400 mt-1.5 leading-relaxed">
                    Default amount deducted when a project enters production.
                  </p>
                </div>

                <div className="border-t border-gray-100 pt-4">
                  <label className="text-[11px] uppercase tracking-[0.15em] text-gray-400 block mb-1.5">
                    Internal notes (optional)
                  </label>
                  <textarea
                    data-testid="settings-notes"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Comments about recent changes…"
                    className="w-full bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 outline-none focus:border-gray-300 transition-colors resize-none"
                    rows={3}
                  />
                </div>
              </div>

              {feedback && (
                <div
                  data-testid="settings-feedback"
                  className={`flex items-center gap-2 text-sm rounded-xl px-4 py-2.5 border ${
                    feedback.type === 'ok'
                      ? 'text-emerald-700 bg-emerald-50 border-emerald-100'
                      : 'text-rose-700 bg-rose-50 border-rose-100'
                  }`}
                >
                  {feedback.type === 'ok' ? <Check className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
                  {feedback.msg}
                </div>
              )}

              <div className="flex items-center justify-between gap-3">
                <span className="text-[11px] text-gray-400">
                  {updatedAt ? `Last updated: ${new Date(updatedAt).toLocaleString('en-US')}` : ''}
                </span>
                <Button
                  data-testid="settings-save-btn"
                  onClick={handleSave}
                  disabled={saving}
                  className="bg-gray-900 hover:bg-gray-800 text-white rounded-xl px-5"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-2" />Save</>}
                </Button>
              </div>

              <ManagerAccessPanel />
            </>
          )}
        </div>
      </ScrollArea>
    </div>
  );
}

// ---------- Hidden manager access (Phase 3) ---------------------------------

function ManagerAccessPanel() {
  const { session } = useAuth();
  const [busy, setBusy] = useState(null); // 'secret'|'rotate'|null
  const [secretSet, setSecretSet] = useState(null);
  const [linkToken, setLinkToken] = useState(null);
  const [secret, setSecret] = useState('');
  const [showSecret, setShowSecret] = useState(false);
  const [feedback, setFeedback] = useState(null);

  const refresh = async () => {
    if (!session?.access_token) return;
    const { ok, data } = await xhrJson('GET', `${API}/api/manager/auth/state`, null, authHeader(session));
    if (ok) {
      setSecretSet(!!data.secret_set);
      setLinkToken(data.link_token || null);
    }
  };
  useEffect(() => { refresh(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [session?.access_token]);

  const saveSecret = async () => {
    if (secret.trim().length < 4) {
      setFeedback({ type: 'err', msg: 'Minimum 4 characters.' });
      return;
    }
    setBusy('secret'); setFeedback(null);
    const { ok, data } = await xhrJson('POST', `${API}/api/manager/auth/set-secret`,
      { secret: secret.trim() }, authHeader(session));
    setBusy(null);
    if (ok) {
      setSecret(''); setSecretSet(true);
      setFeedback({ type: 'ok', msg: 'Secret saved. You can now use /manager <secret> in the public chat.' });
    } else {
      setFeedback({ type: 'err', msg: data?.detail || "Couldn't save." });
    }
  };

  const rotate = async () => {
    setBusy('rotate'); setFeedback(null);
    const { ok, data } = await xhrJson('POST', `${API}/api/manager/auth/rotate-token`,
      {}, authHeader(session));
    setBusy(null);
    if (ok) {
      setLinkToken(data.token);
      setFeedback({ type: 'ok', msg: 'Token rotated. The previous link no longer works.' });
    } else {
      setFeedback({ type: 'err', msg: data?.detail || "Couldn't rotate." });
    }
  };

  const link = linkToken ? `${window.location.origin}/m/${linkToken}` : '';
  const copyLink = () => {
    if (!link) return;
    navigator.clipboard?.writeText(link);
    setFeedback({ type: 'ok', msg: 'Link copied.' });
  };

  return (
    <div data-testid="manager-access-panel" className="border border-gray-200/60 rounded-2xl p-5 bg-white/40 backdrop-blur-sm space-y-5">
      <div className="flex items-center gap-2">
        <Lock className="w-4 h-4 text-gray-400" />
        <h2 className="text-sm font-medium text-gray-900 font-['Outfit']">Hidden access</h2>
      </div>

      {/* Secret command */}
      <div>
        <label className="text-[11px] uppercase tracking-[0.15em] text-gray-400 block mb-1.5">
          Secret command in the sales chat
        </label>
        <p className="text-[11px] text-gray-500 mb-2 leading-relaxed">
          Anyone who types <code className="font-mono bg-gray-100 px-1 rounded">/manager &lt;secret&gt;</code> in the public chat and has the manager role will enter the panel.
          Current state: {secretSet === null ? <span className="text-gray-300">loading…</span> : secretSet
            ? <span className="text-emerald-600 font-medium">configured</span>
            : <span className="text-amber-600 font-medium">not configured</span>}
        </p>
        <div className="flex gap-2">
          <input
            data-testid="settings-secret-input"
            type={showSecret ? 'text' : 'password'}
            value={secret}
            onChange={(e) => setSecret(e.target.value)}
            placeholder={secretSet ? 'Change secret…' : 'Set a secret…'}
            className="flex-1 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 outline-none focus:border-gray-300 transition-colors font-mono"
          />
          <button
            data-testid="settings-secret-toggle"
            onClick={() => setShowSecret((v) => !v)}
            className="text-[11px] text-gray-500 px-2"
          >
            {showSecret ? 'Hide' : 'Show'}
          </button>
          <Button
            data-testid="settings-secret-save"
            onClick={saveSecret}
            disabled={busy === 'secret' || !secret.trim()}
            size="sm"
            className="bg-gray-900 hover:bg-gray-800 text-white text-xs rounded-xl"
          >
            {busy === 'secret' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Save'}
          </Button>
        </div>
      </div>

      {/* Magic link */}
      <div className="border-t border-gray-100 pt-4">
        <label className="text-[11px] uppercase tracking-[0.15em] text-gray-400 block mb-1.5">
          Magic link to the manager panel
        </label>
        <p className="text-[11px] text-gray-500 mb-2 leading-relaxed">
          Share this link with authorized people. It only grants access if their account already has the manager role.
        </p>
        {linkToken ? (
          <div className="flex items-center gap-2">
            <div className="flex-1 flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-[11px] text-gray-700 font-mono truncate">
              <LinkIcon className="w-3.5 h-3.5 text-gray-400 shrink-0" />
              <span data-testid="settings-link-value" className="truncate">{link}</span>
            </div>
            <button
              data-testid="settings-link-copy"
              onClick={copyLink}
              className="text-[11px] text-gray-500 px-2 hover:text-gray-900"
              title="Copy link"
            >
              <Copy className="w-3.5 h-3.5" />
            </button>
            <Button
              data-testid="settings-link-rotate"
              onClick={rotate}
              disabled={busy === 'rotate'}
              size="sm"
              variant="outline"
              className="text-xs rounded-xl border-gray-200"
            >
              {busy === 'rotate' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><RefreshCw className="w-3.5 h-3.5 mr-1.5" />Rotate</>}
            </Button>
          </div>
        ) : (
          <Button
            data-testid="settings-link-generate"
            onClick={rotate}
            disabled={busy === 'rotate'}
            size="sm"
            className="bg-gray-900 hover:bg-gray-800 text-white text-xs rounded-xl"
          >
            {busy === 'rotate' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Generate link'}
          </Button>
        )}
      </div>

      {feedback && (
        <div
          data-testid="manager-access-feedback"
          className={`flex items-center gap-2 text-xs rounded-lg px-3 py-2 border ${
            feedback.type === 'ok'
              ? 'text-emerald-700 bg-emerald-50 border-emerald-100'
              : 'text-rose-700 bg-rose-50 border-rose-100'
          }`}
        >
          {feedback.type === 'ok' ? <Check className="w-3.5 h-3.5" /> : <AlertCircle className="w-3.5 h-3.5" />}
          {feedback.msg}
        </div>
      )}
    </div>
  );
}
